/**
 * Synthetic patient profile fields for the demo PMS mockup.
 * Values are deterministic per `patientId` so the overview stays stable.
 */

export interface DemoPatientProfile {
  fullName: string;
  dob: string;
  chartNumber: string;
  insurance: string;
  nextVisit: string;
  phone: string;
  email: string;
  address: string;
  provider: string;
}

const INSURANCE_PLANS = [
  'Delta Dental PPO',
  'Cigna DPPO',
  'Aetna DMO',
  'MetLife PDP Plus',
  'Guardian DentalGuard',
  'Humana Dental',
] as const;

const PROVIDERS = ['Dr. A. Rivera', 'Dr. S. Nguyen', 'Dr. K. Brooks', 'Dr. M. Patel'] as const;

const STREETS = [
  '1420 Maple Ave',
  '88 Cedar Ln',
  '501 Brookside Dr',
  '19 Harbor View Rd',
  '3300 Westfield Blvd',
] as const;

function hashCode(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function pick<T>(items: readonly T[], seed: number): T {
  return items[seed % items.length]!;
}

function formatDob(seed: number): string {
  const year = 1948 + (seed % 45);
  const month = 1 + (seed % 12);
  const day = 1 + (seed % 28);
  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
}

function formatNextVisit(seed: number): string {
  const base = new Date();
  base.setDate(base.getDate() + 3 + (seed % 21));
  const hours = 8 + (seed % 8);
  const minutes = seed % 2 === 0 ? '00' : '30';
  return base.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }) + ` · ${hours}:${minutes} AM`;
}

function emailFromName(fullName: string): string {
  const parts = fullName.toLowerCase().replace(/[^a-z\s]/g, '').trim().split(/\s+/);
  if (parts.length < 2) return `${parts[0] || 'patient'}@example-mail.com`;
  return `${parts[0]}.${parts[parts.length - 1]}@example-mail.com`;
}

/**
 * @param patientId - Sandbox patient id from the dropdown
 * @param fullName - Display name for the selected patient
 * @returns Hardcoded-looking profile data unique to this patient
 */
export function getDemoPatientProfile(patientId: string, fullName: string): DemoPatientProfile {
  const seed = hashCode(patientId);
  const chartSuffix = patientId.slice(-6).padStart(6, '0');
  const area = 200 + (seed % 700);
  const exchange = 100 + ((seed >> 3) % 900);
  const line = 1000 + ((seed >> 6) % 9000);

  return {
    fullName,
    dob: formatDob(seed),
    chartNumber: `CH-${chartSuffix}`,
    insurance: pick(INSURANCE_PLANS, seed),
    nextVisit: formatNextVisit(seed >> 2),
    phone: `(${area}) ${exchange}-${line}`,
    email: emailFromName(fullName),
    address: `${pick(STREETS, seed >> 4)}, Suite ${100 + (seed % 400)}`,
    provider: pick(PROVIDERS, seed >> 5),
  };
}

const EMPTY_PLACEHOLDER = '— —';
const EMPTY_NAME_LABEL = 'Patient overview';

/**
 * Formats a "First [Middle] Last" display name for PMS mockups that render
 * names differently (e.g. Dentrix/OpenDental title bars show "Last, First").
 *
 * @param fullName - Space-separated display name from the demo patient list
 * @param format - `last-first` | `last` | `first`; anything else returns the full name
 * @returns The formatted name (single-word names pass through unchanged)
 */
export function formatPatientName(fullName: string, format: string | undefined): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]! : '';
  switch (format) {
    case 'last-first':
      return last ? `${last}, ${first}` : first;
    case 'last':
      return last || first;
    case 'first':
      return first;
    default:
      return fullName;
  }
}

/**
 * Updates the demo PMS patient overview and demographics from the selection.
 *
 * Name slots are opt-in per page: `#patient-overview-name` is the primary one,
 * and any number of `[data-patient-name]` elements act as extra slots (the
 * CareStack page uses one for its Account Summary cell). Either kind may carry
 * `data-empty-label` to override the cleared-state placeholder and
 * `data-name-format` (see {@link formatPatientName}) to render e.g.
 * "Last, First" for the Dentrix/OpenDental title bars. The remaining
 * overview/demographics fields only exist on the richer mock pages, so each is
 * updated only when present.
 *
 * @param patientId - Selected patient id, or empty when cleared
 * @param fullName - Label for the selected patient
 */
export function renderDemoPatientProfile(patientId: string, fullName: string): void {
  const nameEl = document.getElementById('patient-overview-name');
  // Extra live-name slots (e.g. the CareStack account-summary overlay).
  const taggedNameEls = document.querySelectorAll<HTMLElement>('[data-patient-name]');
  const dobEl = document.getElementById('patient-overview-dob');
  const chartEl = document.getElementById('patient-overview-chart');
  const insuranceEl = document.getElementById('patient-overview-insurance');
  const nextVisitEl = document.getElementById('patient-overview-next-visit');
  const phoneEl = document.getElementById('patient-demo-phone');
  const emailEl = document.getElementById('patient-demo-email');
  const addressEl = document.getElementById('patient-demo-address');
  const providerEl = document.getElementById('patient-demo-provider');
  const demographicsEl = document.getElementById('patient-demographics');
  const odontogramCardEl = document.getElementById('patient-odontogram-card');
  const scheduleCardEl = document.getElementById('patient-schedule-card');

  taggedNameEls.forEach((el) => {
    el.textContent = patientId
      ? formatPatientName(fullName, el.dataset.nameFormat)
      : el.dataset.emptyLabel ?? EMPTY_NAME_LABEL;
  });

  if (!nameEl) return;

  if (!patientId) {
    nameEl.textContent = nameEl.dataset.emptyLabel ?? EMPTY_NAME_LABEL;
    if (dobEl) dobEl.textContent = `DOB ${EMPTY_PLACEHOLDER}`;
    if (chartEl) chartEl.textContent = `Chart # ${EMPTY_PLACEHOLDER}`;
    if (insuranceEl) insuranceEl.textContent = `Insurance ${EMPTY_PLACEHOLDER}`;
    if (nextVisitEl) nextVisitEl.textContent = `Next visit ${EMPTY_PLACEHOLDER}`;
    if (phoneEl) phoneEl.textContent = EMPTY_PLACEHOLDER;
    if (emailEl) emailEl.textContent = EMPTY_PLACEHOLDER;
    if (addressEl) addressEl.textContent = EMPTY_PLACEHOLDER;
    if (providerEl) providerEl.textContent = EMPTY_PLACEHOLDER;
    demographicsEl?.classList.add('is-empty');
    odontogramCardEl?.classList.add('is-hidden');
    scheduleCardEl?.classList.add('is-hidden');
    return;
  }

  const profile = getDemoPatientProfile(patientId, fullName);
  nameEl.textContent = formatPatientName(profile.fullName, nameEl.dataset.nameFormat);
  if (dobEl) dobEl.textContent = `DOB ${profile.dob}`;
  if (chartEl) chartEl.textContent = `Chart # ${profile.chartNumber}`;
  if (insuranceEl) insuranceEl.textContent = profile.insurance;
  if (nextVisitEl) nextVisitEl.textContent = `Next visit ${profile.nextVisit}`;
  if (phoneEl) phoneEl.textContent = profile.phone;
  if (emailEl) emailEl.textContent = profile.email;
  if (addressEl) addressEl.textContent = profile.address;
  if (providerEl) providerEl.textContent = profile.provider;
  demographicsEl?.classList.remove('is-empty');
  odontogramCardEl?.classList.remove('is-hidden');
  scheduleCardEl?.classList.remove('is-hidden');
}
